import React from 'react'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  CloseButton,
  Icon,
} from '@chakra-ui/react'

/**
 * Props for the StaticBanner component
 */
export interface StaticBannerIProps {
  message: string
  status: 'warning' | 'error' | 'info' | 'success'
  onDismiss?: () => void
  dismissible?: boolean
  icon?: React.ReactElement
}

/**
 * Get the color scheme based on alert status
 * Used for both icon and text to maintain consistency
 */
const getStatusColor = (status: 'warning' | 'error' | 'info' | 'success') => {
  const colorMap = {
    error: 'red.600',
    warning: 'orange.600',
    info: 'blue.600',
    success: 'green.600',
  }
  return colorMap[status]
}

/**
 * Static banner component for displaying product messages.
 */
export const StaticBanner: React.FC<StaticBannerIProps> = ({
  message,
  status,
  onDismiss,
  dismissible = true,
  icon,
}) => {
  const statusColor = getStatusColor(status)

  const renderIcon = () => {
    if (icon) {
      return (
        <Icon
          aria-hidden='true'
          as={icon.type}
          boxSize='6'
          color={statusColor}
          marginEnd='3'
          {...icon.props}
        />
      )
    }
    return <AlertIcon aria-hidden='true' />
  }

  return (
    <Box
      aria-label='Product announcement'
      role='banner'
      width='100%'
      zIndex='banner'
    >
      <Alert
        borderRadius='0'
        fontSize='sm'
        justifyContent='center'
        px='6'
        py='3'
        role='alert'
        status={status}
      >
        {renderIcon()}
        <AlertDescription
          color={statusColor}
          fontWeight='medium'
          maxWidth='container.lg'
          textAlign='center'
        >
          {message}
        </AlertDescription>
        {dismissible && onDismiss ? (
          <CloseButton
            aria-label='Dismiss announcement'
            onClick={onDismiss}
            position='absolute'
            right='8px'
            top='8px'
          />
        ) : null}
      </Alert>
    </Box>
  )
}
